const DEFAULT_BASE_URL = "https://watsybackend.teccube.teccube.ly";

export const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || DEFAULT_BASE_URL
).replace(/\/+$/, "");

const AUTH_STORAGE_KEYS = [
  "token",
  "access_token",
  "refresh_token",
  "user",
  "role",
];
const inflightGetRequests = new Map();

export function clearAuthSession() {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

function localizedMessage(payload, fallback) {
  const lang = localStorage.getItem("lang") || "ar";
  const localizedError = payload?.error;

  if (typeof localizedError === "string") return localizedError;
  if (localizedError && typeof localizedError === "object") {
    return (
      localizedError[lang] ||
      localizedError.ar ||
      localizedError.en ||
      Object.values(localizedError).find(Boolean) ||
      fallback
    );
  }

  if (Array.isArray(payload?.errors) && payload.errors[0]) {
    return payload.errors[0].message || payload.errors[0].reason || fallback;
  }

  return payload?.message || payload?.msg || fallback;
}

async function parseResponse(response) {
  if (response.status === 204) return null;

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  return text ? { message: text } : null;
}

/**
 * Small API client shared by the dashboard.
 * The access token is read for every request so a login in the same tab is
 * immediately available without reloading the JavaScript bundle.
 */
async function performRequest(
  endpoint,
  method = "GET",
  body,
  options = {}
) {
  const normalizedEndpoint = String(endpoint || "").replace(/^\/+/, "");
  const url = `${API_BASE_URL}/${normalizedEndpoint}`;
  const token = localStorage.getItem("access_token");
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const requestMethod = method.toUpperCase();

  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (body !== undefined && body !== null && !isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  let response;
  try {
    response = await fetch(url, {
      ...options,
      method: requestMethod,
      headers,
      body,
    });
  } catch (error) {
    const networkError = new Error(
      localStorage.getItem("lang") === "en"
        ? "We could not reach the server. Check your connection and try again."
        : "تعذر الاتصال بالخادم. تحقق من اتصالك وحاول مرة أخرى."
    );
    networkError.cause = error;
    throw networkError;
  }

  const payload = await parseResponse(response);

  if (!response.ok) {
    const fallback =
      localStorage.getItem("lang") === "en"
        ? "The request could not be completed."
        : "تعذر إكمال الطلب.";
    const rawMessage = localizedMessage(payload, fallback);
    const tokenProblem =
      response.status === 401 ||
      /token has expired|invalid signature|not enough segments|missing authorization|bad authorization/i.test(
        rawMessage
      );
    const error = new Error(
      tokenProblem && token
        ? localStorage.getItem("lang") === "en"
          ? "Your session expired. Sign in to continue."
          : "انتهت جلسة الدخول. سجل الدخول للمتابعة."
        : rawMessage
    );
    error.status = response.status;
    error.payload = payload;

    const isAuthRequest = ["login", "signup", "send_otp", "verify_otp"].some(
      (path) => normalizedEndpoint.startsWith(path)
    );
    if (tokenProblem && token && !isAuthRequest) {
      clearAuthSession();
      window.location.replace("/auth/login?expired=1");
    }

    throw error;
  }

  return payload;
}

export default function fetchData(endpoint, method = "GET", body, options = {}) {
  const requestMethod = String(method).toUpperCase();
  if (requestMethod !== "GET" || body !== undefined || options.dedupe === false) {
    return performRequest(endpoint, method, body, options);
  }

  const normalizedEndpoint = String(endpoint || "").replace(/^\/+/, "");
  const token = localStorage.getItem("access_token") || "anonymous";
  const key = `${token}:${normalizedEndpoint}`;
  const existing = inflightGetRequests.get(key);
  if (existing) return existing;

  const requestPromise = performRequest(endpoint, method, body, options).finally(() => {
    inflightGetRequests.delete(key);
  });
  inflightGetRequests.set(key, requestPromise);
  return requestPromise;
}
